// ResetPassword.jsx
// Page to handle password reset from email link

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { styles } from './styles';
import { LockIcon, EyeIcon, EyeOffIcon, CheckCircleIcon } from '../../components/Icons';

const ResetPassword = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const oobCode = params.get('oobCode');
  const mode = params.get('mode');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  // Verify the reset code on mount
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode || mode !== 'resetPassword') {
        setError('Invalid password reset link. Please request a new one.');
        setVerifying(false);
        return;
      }

      try {
        // Verify the code and get the user's email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setVerifying(false);
      } catch (err) {
        if (err.code === 'auth/invalid-action-code') {
          setError('This password reset link has expired or was already used. Please request a new one.');
        } else if (err.code === 'auth/expired-action-code') {
          setError('This password reset link has expired. Please request a new one.');
        } else {
          setError('Invalid password reset link. Please request a new one.');
        }
        setVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode, mode]);

  const validatePassword = (pwd) => {
    if (!pwd) {
      return 'Please enter a password.';
    }
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters long.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This password reset link has expired or was already used.');
      } else {
        setError('Failed to reset password. Please try again or request a new reset link.');
      }
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundDecoration}></div>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.iconContainer}>
              <div style={styles.logoIcon}>
                <img 
                  src="/favicon_io/android-chrome-192x192.png" 
                  alt="Redbird Markets Logo" 
                  style={styles.logoImage}
                />
              </div>
            </div>
            <h1 style={styles.title}>Verifying Reset Link...</h1>
          </div>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !email) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundDecoration}></div>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.iconContainer}>
              <div style={styles.errorIcon}>✕</div>
            </div>
            <h1 style={styles.title}>Invalid Reset Link</h1>
            <p style={styles.subtitle}>{error}</p>
          </div>
          <button
            onClick={() => navigate('/forgot-password')}
            style={styles.submitButton}
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundDecoration}></div>
        <div style={styles.card}>
          <div style={styles.successIconContainer}>
            <CheckCircleIcon size={64} color="#22C55E" />
          </div>
          <h1 style={styles.successTitle}>Password Reset Successful!</h1>
          <p style={styles.successText}>
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <div style={styles.redirectText}>
            Redirecting to login in 3 seconds...
          </div>
          <button
            onClick={() => navigate('/login')}
            style={styles.submitButton}
          >
            Go to Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.backgroundDecoration}></div>
      
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <div style={styles.logoIcon}>
              <img 
                src="/favicon_io/android-chrome-192x192.png" 
                alt="Redbird Markets Logo" 
                style={styles.logoImage}
              />
            </div>
          </div>
          <h1 style={styles.title}>Set New Password</h1>
          <p style={styles.subtitle}>
            Enter a new password for {email}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* New Password Field */}
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel} htmlFor="password">
              New Password
            </label>
            <div style={styles.inputWrapper}>
              <LockIcon 
                size={20} 
                color="#666" 
                style={styles.inputIcon}
              />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === 'password' ? styles.inputFocused : {}),
                  paddingRight: '48px'
                }}
                required
                disabled={loading}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOffIcon size={20} color="#666" />
                ) : (
                  <EyeIcon size={20} color="#666" />
                )}
              </button>
            </div>
            <p style={styles.helperText}>Must be at least 6 characters</p>
          </div>

          {/* Confirm Password Field */}
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel} htmlFor="confirmPassword">
              Confirm New Password
            </label>
            <div style={styles.inputWrapper}>
              <LockIcon 
                size={20} 
                color="#666" 
                style={styles.inputIcon}
              />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === 'confirmPassword' ? styles.inputFocused : {}),
                  paddingRight: '48px'
                }}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.eyeButton}
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOffIcon size={20} color="#666" />
                ) : (
                  <EyeIcon size={20} color="#666" />
                )}
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              ...(loading ? styles.submitButtonDisabled : {})
            }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        {/* Back to Login Link */}
        <div style={styles.backLinkContainer}>
          <button
            onClick={() => navigate('/login')}
            style={styles.backLink}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
